import { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { dangerBtn, primaryBtn, secondaryBtn } from "../styles";

interface Props {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  confirming?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Extra content rendered between the message and the action buttons — e.g. an
   *  impact preview for a destructive action. Optional; most callers don't need it. */
  children?: ReactNode;
  /** Overrides the computed (danger ? dangerBtn : primaryBtn) confirm-button style —
   *  e.g. dangerFilledBtn for an action with no undo, stronger than the default danger look. */
  confirmButtonStyle?: CSSProperties;
}

export default function ConfirmModal({
  title,
  message,
  confirmLabel,
  danger = true,
  confirming = false,
  onConfirm,
  onCancel,
  children,
  confirmButtonStyle,
}: Props) {
  const { t } = useTranslation();

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "var(--color-surface)",
          border: "1px solid color-mix(in srgb, var(--color-accent) 25%, var(--color-border))",
          borderRadius: "16px",
          // Fixed padding on a 90%-of-320px box left little usable content at the smallest
          // supported width (CLAUDE.md: >=320px). min() with vw shrinks padding on narrow
          // viewports, matching the shared `modal` style's width convention in styles.ts.
          padding: "min(1.75rem, 6vw)",
          width: "min(420px, 92vw)",
          boxShadow: "0 0 0 1px color-mix(in srgb, var(--color-accent) 12%, transparent), 0 24px 64px var(--color-shadow)",
        }}
      >
        <h3 style={{ margin: "0 0 0.75rem", fontSize: "1.1rem", color: "var(--color-text)" }}>{title}</h3>
        <p style={{ margin: children ? "0 0 1rem" : "0 0 1.5rem", color: "var(--color-text-muted)", lineHeight: 1.55 }}>{message}</p>
        {children && <div style={{ margin: "0 0 1.5rem" }}>{children}</div>}
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button onClick={onCancel} disabled={confirming} style={secondaryBtn}>
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={confirming}
            style={{ ...(confirmButtonStyle ?? (danger ? dangerBtn : primaryBtn)), opacity: confirming ? 0.6 : 1 }}
          >
            {confirming ? "…" : (confirmLabel ?? t("common.confirm"))}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
